import React from 'react';
import { motion } from 'framer-motion';
import { FileCheck, Wrench, Package, Banknote, Star } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const ICONS = { FileCheck, Wrench, Package, Banknote };

export default function Investments() {
  const { t } = useLang();
  const inv = t.investments;

  return (
    <section id="investments" className="py-32 md:py-40 relative overflow-hidden" style={{ background: '#0A0B0F' }}>
      {/* Background image with overlay */}
      <div className="section-bg">
        <img src="/img/photo_20_2026-03-14_14-37-22.jpg" alt="" />
      </div>

      <div
        className="orb-gold absolute"
        style={{ width: 800, height: 800, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', opacity: 0.1, zIndex: 1 }}
      />
      <div
        className="orb-purple absolute"
        style={{ width: 600, height: 600, bottom: '-15%', right: '-10%', opacity: 0.08, zIndex: 1 }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Enhanced Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mb-20"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-bold">Investment</span>
          </div>
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-6 font-heading leading-[0.95]" style={{ fontFamily: "'Sora', sans-serif", textShadow: '0 2px 30px rgba(0,0,0,0.5)' }}>{inv.title}</h2>
          <p className="text-[#BFC3C9] text-xl md:text-2xl">{inv.subtitle}</p>
        </motion.div>

        {/* Premium Investment Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {inv.items.map((item, i) => {
            const Icon = ICONS[item.icon] || Banknote;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="glass-premium rounded-3xl p-8 card-premium group"
                data-testid={`investment-item-${i}`}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%)', 
                    border: '1px solid rgba(212,175,55,0.25)',
                    boxShadow: '0 8px 24px rgba(212,175,55,0.1)'
                  }}
                >
                  <Icon size={24} className="text-[#D4AF37]" />
                </div>
                <p className="text-[#9CA3AF] text-sm mb-4 leading-relaxed">{item.label}</p>
                <p className="text-white font-black text-2xl font-heading">{item.value}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Total highlight - Enhanced */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="investment-total rounded-[2rem] p-10 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8"
          style={{
            background: 'linear-gradient(135deg, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0.04) 100%)',
            border: '1px solid rgba(212,175,55,0.3)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(212,175,55,0.2)'
          }}
          data-testid="investment-total"
        >
          <div className="flex items-center gap-5">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ 
                background: 'linear-gradient(135deg, rgba(212,175,55,0.2) 0%, rgba(212,175,55,0.1) 100%)', 
                border: '1px solid rgba(212,175,55,0.4)',
                boxShadow: '0 10px 30px rgba(212,175,55,0.15)'
              }}
            >
              <Star size={32} className="text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-[#D4AF37] text-sm uppercase tracking-widest font-bold mb-2">{inv.total_label}</p>
              <p className="text-[#E5E7EB] text-base">{inv.total_note}</p>
            </div>
          </div>
          <div className="text-center md:text-right">
            <p className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-black font-heading gold-text whitespace-nowrap">{inv.total_value}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
